import type { ISimpleSelectItem } from '../components/select/SimpleSelect';
import { Country } from 'src/types/enums';

export const COUNTRY_VALUES: ISimpleSelectItem[] = Object.values(Country).map((v) => ({
  key: v,
  name: v,
}));

/**
 * Full country name for a 2-letter country code, in the given language.
 *
 * Used for the country line of a postal address, which is written out in full
 * rather than as a code. Falls back to the code itself when it is unknown.
 */
export function getCountryDisplayName(countryCode: string, lang: string): string {
  const code = countryCode.trim().toUpperCase();
  if (code.length !== 2) {
    return code;
  }

  try {
    return new Intl.DisplayNames([lang.toLowerCase()], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

export function parseAddressString(_address: string): { street: string; streetNumber: string } {
  // Clean up the input string
  const address = _address.replace(/\s+/g, ' ').trim();

  // Match common patterns:
  // 1. Street name followed by number and optional extras (e.g., "Lilla Bommen 1, 16 tr")
  // 2. Street name with number (e.g., "Industrieweg 147")
  const patterns = [
    /^(.*?)\s+(\d+(?:[-/]?\w*)?(?:,?\s+\d+\s+\w+)?)\s*$/, // Matches complex numbers with optional floor/apartment
    /^(.*?)\s+(\d+\w*)\s*$/, // Simple street number pattern
  ];

  for (const pattern of patterns) {
    const match = address.match(pattern);
    if (match) {
      return {
        street: match[1].trim(),
        streetNumber: match[2].trim(),
      };
    }
  }

  // If no patterns match, assume it's just a street name
  return {
    street: address.trim(),
    streetNumber: '',
  };
}
